import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { StatCard } from '../components/StatCard';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { SearchBar } from '../components/SearchBar';
import { TableFilter } from '../components/TableFilter';
import type { Filter } from '../components/TableFilter';
import { ExportButton } from '../components/ExportButton';
import { Plus, ArrowUpDown, ArrowUp, ArrowDown, Calendar, User, Link as LinkIcon, CheckCircle2, AlertCircle, Clock, XCircle, Archive, Eye, EyeOff } from 'lucide-react';
import { CreateTaskModal } from '../components/CreateTaskModal';
import type { TaskData, EditTaskData } from '../components/CreateTaskModal';
import { useTasks } from '../contexts/TaskContext';
export function TasksPage() {
  const navigate = useNavigate();
  const {
    tasks,
    addTask,
    updateTask
  } = useTasks();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<EditTaskData | null>(null);
  // Archive state
  const [archivedTaskIds, setArchivedTaskIds] = useState<Set<string>>(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const handleArchiveTask = (taskId: string) => {
    setArchivedTaskIds(prev => new Set([...prev, taskId]));
  };
  const handleUnarchiveTask = (taskId: string) => {
    setArchivedTaskIds(prev => {
      const next = new Set(prev);
      next.delete(taskId);
      return next;
    });
  };
  const handleArchiveAllCompleted = () => {
    const completedIds = tasks.filter(t => t.status === 'Complete').map(t => t.id);
    setArchivedTaskIds(prev => new Set([...prev, ...completedIds]));
  };
  const completedCount = tasks.filter(t => t.status === 'Complete').length;
  const archivedCount = tasks.filter(t => archivedTaskIds.has(t.id)).length;
  // Calculate stats for Sarah Chen
  const myOpenTasks = tasks.filter(t => t.assignee.name === 'Sarah Chen' && t.status !== 'Complete' && t.status !== 'Cancelled').length;
  const overdue = tasks.filter(t => t.status !== 'Complete' && t.status !== 'Cancelled' && new Date(t.dueDate) < new Date('2026-02-12')).length;
  const dueToday = tasks.filter(t => t.status !== 'Complete' && t.status !== 'Cancelled' && new Date(t.dueDate).toDateString() === new Date('2026-02-12').toDateString()).length;
  const completedThisWeek = tasks.filter(t => t.status === 'Complete').length;
  const stats = {
    myOpenTasks,
    overdue,
    dueToday,
    completedThisWeek
  };
  // Filters configuration
  const availableFilters: Filter[] = [{
    id: 'status',
    label: 'Status',
    options: [{
      id: 'open',
      label: 'Open',
      value: 'Open'
    }, {
      id: 'in-progress',
      label: 'In Progress',
      value: 'In Progress'
    }, {
      id: 'complete',
      label: 'Complete',
      value: 'Complete'
    }, {
      id: 'cancelled',
      label: 'Cancelled',
      value: 'Cancelled'
    }],
    selectedValues: filterValues.status || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('status')
  }, {
    id: 'priority',
    label: 'Priority',
    options: [{
      id: 'low',
      label: 'Low',
      value: 'Low'
    }, {
      id: 'medium',
      label: 'Medium',
      value: 'Medium'
    }, {
      id: 'high',
      label: 'High',
      value: 'High'
    }, {
      id: 'urgent',
      label: 'Urgent',
      value: 'Urgent'
    }],
    selectedValues: filterValues.priority || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('priority')
  }, {
    id: 'assignee',
    label: 'Assignee',
    options: [{
      id: 'mj',
      label: 'Mike Johnson',
      value: 'Mike Johnson'
    }, {
      id: 'sc',
      label: 'Sarah Chen',
      value: 'Sarah Chen'
    }, {
      id: 'ct',
      label: 'Chris Taylor',
      value: 'Chris Taylor'
    }],
    selectedValues: filterValues.assignee || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('assignee')
  }, {
    id: 'dueDate',
    label: 'Due Date',
    options: [{
      id: 'overdue',
      label: 'Overdue',
      value: 'overdue'
    }, {
      id: 'today',
      label: 'Due Today',
      value: 'today'
    }, {
      id: 'week',
      label: 'Due This Week',
      value: 'week'
    }, {
      id: 'month',
      label: 'Due This Month',
      value: 'month'
    }],
    selectedValues: filterValues.dueDate || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('dueDate')
  }];
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    setFilterValues(prev => ({
      ...prev,
      [filterId]: Array.isArray(value) ? value : [value]
    }));
  };
  const handleFilterRemove = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.delete(filterId);
    setAddedFilters(newAddedFilters);
    setFilterValues(prev => ({
      ...prev,
      [filterId]: []
    }));
  };
  const handleToggleFilterDropdown = (filterId: string) => {
    const newOpenFilterDropdowns = new Set(openFilterDropdowns);
    if (newOpenFilterDropdowns.has(filterId)) {
      newOpenFilterDropdowns.delete(filterId);
    } else {
      newOpenFilterDropdowns.add(filterId);
    }
    setOpenFilterDropdowns(newOpenFilterDropdowns);
  };
  const handleAddFilter = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.add(filterId);
    setAddedFilters(newAddedFilters);
  };
  const handleClearAll = () => {
    setFilterValues({});
    setAddedFilters(new Set());
    setSearchQuery('');
  };
  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return {
        key,
        direction: 'asc'
      };
    });
  };
  // Filter and Sort Logic
  const filteredTasks = useMemo(() => {
    let result = tasks.filter(task => {
      // Hide archived unless showArchived is on
      if (!showArchived && archivedTaskIds.has(task.id)) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!task.title.toLowerCase().includes(query) && !task.assignee.name.toLowerCase().includes(query) && !task.linkedTo?.id.toLowerCase().includes(query)) {
          return false;
        }
      }
      if (filterValues.status?.length && !filterValues.status.includes(task.status)) return false;
      if (filterValues.priority?.length && !filterValues.priority.includes(task.priority)) return false;
      if (filterValues.assignee?.length && !filterValues.assignee.includes(task.assignee.name)) return false;
      return true;
    });
    if (sortConfig) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key as keyof typeof a];
        const bValue = b[sortConfig.key as keyof typeof b];
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [tasks, searchQuery, filterValues, sortConfig, showArchived, archivedTaskIds]);
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'Urgent':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-9548">
            Urgent
          </span>;
      case 'High':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800" data-id="element-9549">
            High
          </span>;
      case 'Medium':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-9550">
            Medium
          </span>;
      case 'Low':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-9551">
            Low
          </span>;
      default:
        return null;
    }
  };
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Open':
        return 'info';
      case 'In Progress':
        return 'warning';
      case 'Complete':
        return 'success';
      case 'Cancelled':
        return 'default';
      default:
        return 'default';
    }
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-9552">
      <div className="mb-6" data-id="element-9553">
        <NavBar tabs={[]} title="Tasks" subtitle="Manage and track team tasks and follow-ups" data-id="element-9554" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-9555">
        <StatCard label="My Open Tasks" value={stats.myOpenTasks} data-id="element-9556" />
        <StatCard label="Overdue" value={stats.overdue} trend={<span className="text-xs font-medium text-[#EF4444]" data-id="element-9558">
              Needs Attention
            </span>} data-id="element-9557" />
        <StatCard label="Due Today" value={stats.dueToday} data-id="element-9559" />
        <StatCard label="Completed This Week" value={stats.completedThisWeek} data-id="element-9560" />
      </div>

      <hr className="my-10 border-[#CBD5E1]" data-id="element-9561" />

      {/* Actions Bar */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg mb-6 p-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-9562">
        <div className="flex flex-col gap-4" data-id="element-9563">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-9564">
            <div className="w-full lg:w-80 flex-shrink-0" data-id="element-9565">
              <SearchBar placeholder="Search tasks..." value={searchQuery} onChange={setSearchQuery} className="w-full" data-id="element-9566" />
            </div>

            <div className="flex items-center gap-3 flex-shrink-0 ml-auto" data-id="element-9567">
              {/* Show/Hide Archived Toggle */}
              <button onClick={() => setShowArchived(!showArchived)} className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-medium border transition-colors ${showArchived ? 'bg-[#F0F9FF] border-[#0EA5E9] text-[#0EA5E9]' : 'bg-white border-[#E2E8F0] text-[#64748B] hover:border-[#94A3B8] hover:text-[#475569]'}`} data-id="element-9568">
                {showArchived ? <Eye className="w-4 h-4" data-id="element-9569" /> : <EyeOff className="w-4 h-4" data-id="element-9570" />}
                {showArchived ? `Showing archived (${archivedCount})` : `${archivedCount} archived`}
              </button>

              {/* Archive All Completed */}
              {completedCount > 0 && <button onClick={handleArchiveAllCompleted} className="flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-medium border border-[#E2E8F0] bg-white text-[#64748B] hover:border-[#94A3B8] hover:text-[#475569] transition-colors" data-id="element-9571">
                  <Archive className="w-4 h-4" data-id="element-9572" />
                  Archive completed
                </button>}

              <ExportButton variant="tasks" onExport={async type => console.log(`Exporting ${type}`)} data-id="element-9573" />

              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-9574" />

              <Button variant="primary" iconLeft={<Plus className="w-4 h-4" data-id="element-9576" />} onClick={() => {
              setEditingTask(null);
              setIsCreateTaskModalOpen(true);
            }} data-id="element-9575">
                Create Task
              </Button>
            </div>
          </div>

          {addedFilters.size > 0 && <div className="flex items-center gap-3" data-id="element-9577">
              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-9578" />
              <button onClick={handleClearAll} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-9579">
                Clear all
              </button>
            </div>}
        </div>
      </div>

      {/* Tasks Table */}
      <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-9580">
        <div className="overflow-x-auto" data-id="element-9581">
          <table className="w-full" data-id="element-9582">
            <thead className="bg-[#F1F5F9]" data-id="element-9583">
              <tr data-id="element-9584">
                <th className="px-4 py-3 w-12" data-id="element-9585">
                  <span className="sr-only" data-id="element-9586">Complete</span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-24" data-id="element-9587">
                  <button onClick={() => handleSort('priority')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-9588">
                    Priority
                    {sortConfig?.key === 'priority' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9589" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9590" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-9591" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-9592">
                  <button onClick={() => handleSort('title')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-9593">
                    Title
                    {sortConfig?.key === 'title' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9594" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9595" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-9596" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-48" data-id="element-9597">
                  <button onClick={() => handleSort('assignee')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-9598">
                    Assignee
                    {sortConfig?.key === 'assignee' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9599" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9600" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-9601" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-32" data-id="element-9602">
                  <button onClick={() => handleSort('dueDate')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-9603">
                    Due Date
                    {sortConfig?.key === 'dueDate' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9604" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9605" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-9606" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-40" data-id="element-9607">
                  Linked To
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-32" data-id="element-9608">
                  <button onClick={() => handleSort('status')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-9609">
                    Status
                    {sortConfig?.key === 'status' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9610" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9611" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-9612" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-40" data-id="element-9613">
                  Created By
                </th>
                <th className="px-4 py-3 w-20" data-id="element-9614">
                  <span className="sr-only" data-id="element-9615">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]" data-id="element-9616">
              {filteredTasks.map(task => {
              const isCompleted = task.status === 'Complete';
              const isArchived = archivedTaskIds.has(task.id);
              return <tr key={task.id} className={`hover:bg-[#F8FAFC] transition-colors cursor-pointer ${isArchived ? 'bg-[#F8FAFC]' : ''}`} onClick={() => {
                setEditingTask({
                  id: task.id,
                  title: task.title,
                  assignee: task.assignee.name,
                  dueDate: task.dueDate,
                  priority: task.priority,
                  linkedTo: task.linkedTo
                });
                setIsCreateTaskModalOpen(true);
              }} data-id="element-9617">
                    {/* Checkbox */}
                    <td className="px-4 py-4" data-id="element-9618">
                      <button onClick={e => {
                    e.stopPropagation();
                    if (isCompleted) {
                      updateTask(task.id, {
                        status: 'Open',
                        completedAt: undefined,
                        completedBy: undefined
                      });
                    } else {
                      updateTask(task.id, {
                        status: 'Complete',
                        completedAt: new Date().toISOString(),
                        completedBy: 'Sarah Chen'
                      });
                    }
                  }} className={`w-[18px] h-[18px] rounded border-2 flex items-center justify-center transition-colors ${isCompleted ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1] hover:border-[#94A3B8]'}`} data-id="element-9619">
                        {isCompleted && <svg width="10" height="10" viewBox="0 0 10 10" fill="none" data-id="element-9620">
                            <path d="M2 5L4.5 7.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-9621" />
                          </svg>}
                      </button>
                    </td>
                    <td className="px-4 py-4" data-id="element-9622">
                      <span className={isCompleted ? 'opacity-50' : ''} data-id="element-9623">
                        {getPriorityBadge(task.priority)}
                      </span>
                    </td>
                    <td className="px-4 py-4" data-id="element-9624">
                      <span className={`text-sm font-medium transition-colors ${isCompleted ? 'text-[#94A3B8] line-through' : 'text-[#0F172A] hover:text-[#0EA5E9]'}`} data-id="element-9625">
                        {task.title}
                      </span>
                    </td>
                    <td className="px-4 py-4" data-id="element-9626">
                      <div className={`flex items-center gap-2 ${isCompleted ? 'opacity-50' : ''}`} data-id="element-9627">
                        <div className="w-6 h-6 rounded-full bg-[#E0F2FE] text-[#0EA5E9] flex items-center justify-center text-xs font-medium" data-id="element-9628">
                          {task.assignee.avatar}
                        </div>
                        <span className="text-sm text-[#475569]" data-id="element-9629">
                          {task.assignee.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-9630">
                      <div className={`flex items-center gap-1.5 ${isCompleted ? 'opacity-50' : ''}`} data-id="element-9631">
                        <Calendar className="w-3.5 h-3.5 text-[#94A3B8]" data-id="element-9632" />
                        <span className={`text-sm ${!isCompleted && new Date(task.dueDate) < new Date('2026-02-12') ? 'text-[#EF4444] font-medium' : 'text-[#475569]'}`} data-id="element-9633">
                          {task.dueDate}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-9634">
                      <span className={isCompleted ? 'opacity-50' : ''} data-id="element-9635">
                        {task.linkedTo ? <button className="flex items-center gap-1.5 text-sm text-[#0EA5E9] hover:text-[#0284C7] hover:underline font-medium transition-colors" onClick={e => {
                      e.stopPropagation();
                      console.log('Navigate to', task.linkedTo?.id);
                    }} data-id="element-9636">
                            <LinkIcon className="w-3.5 h-3.5" data-id="element-9637" />
                            {task.linkedTo.label || task.linkedTo.id}
                          </button> : <span className="text-sm text-[#94A3B8]" data-id="element-9638">—</span>}
                      </span>
                    </td>
                    <td className="px-4 py-4" data-id="element-9639">
                      <Badge variant={getStatusBadgeVariant(task.status)} data-id="element-9640">
                        {task.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-4" data-id="element-9641">
                      <span className={`text-sm text-[#475569] ${isCompleted ? 'opacity-50' : ''}`} data-id="element-9642">
                        {task.createdBy}
                      </span>
                    </td>
                    {/* Archive/Unarchive action */}
                    <td className="px-4 py-4" data-id="element-9643">
                      {isCompleted && <button onClick={e => {
                    e.stopPropagation();
                    if (isArchived) {
                      handleUnarchiveTask(task.id);
                    } else {
                      handleArchiveTask(task.id);
                    }
                  }} className={`text-xs font-medium px-2 py-1 rounded transition-colors ${isArchived ? 'text-[#0EA5E9] hover:bg-[#F0F9FF]' : 'text-[#94A3B8] hover:text-[#64748B] hover:bg-[#F1F5F9]'}`} title={isArchived ? 'Unarchive' : 'Archive'} data-id="element-9644">
                          {isArchived ? 'Unarchive' : 'Archive'}
                        </button>}
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Empty state */}
        {filteredTasks.length === 0 && <div className="py-12 text-center" data-id="element-9645">
            <p className="text-sm text-[#94A3B8]" data-id="element-9646">No tasks found</p>
          </div>}
      </div>

      <CreateTaskModal isOpen={isCreateTaskModalOpen} onClose={() => {
      setIsCreateTaskModalOpen(false);
      setEditingTask(null);
    }} editTask={editingTask} onCreate={taskData => {
      addTask({
        ...taskData,
        status: 'Open',
        createdBy: 'Sarah Chen',
        linkedTo: taskData.linkedTo ? {
          id: taskData.linkedTo.id,
          type: taskData.linkedTo.type as any,
          label: taskData.linkedTo.label
        } : null,
        assignee: {
          name: taskData.assignee,
          avatar: taskData.assignee.split(' ').map(n => n[0]).join(''),
          email: `${taskData.assignee.toLowerCase().replace(' ', '.')}@acmewine.com`
        }
      });
      setIsCreateTaskModalOpen(false);
      setEditingTask(null);
    }} onUpdate={(taskId, taskData) => {
      // Convert the date back to the format used in context (e.g. "Feb 18 2026")
      const dueDateObj = new Date(taskData.dueDate + 'T00:00:00');
      const formattedDate = dueDateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }).replace(',', '');
      updateTask(taskId, {
        title: taskData.title,
        priority: taskData.priority,
        dueDate: formattedDate,
        assignee: {
          name: taskData.assignee,
          avatar: taskData.assignee.split(' ').map(n => n[0]).join(''),
          email: `${taskData.assignee.toLowerCase().replace(' ', '.')}@acmewine.com`
        },
        linkedTo: taskData.linkedTo ? {
          id: taskData.linkedTo.id,
          type: taskData.linkedTo.type as any,
          label: taskData.linkedTo.label
        } : null
      });
      setIsCreateTaskModalOpen(false);
      setEditingTask(null);
    }} data-id="element-9647" />
    </div>;
}